import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../api';

import * as FormUtils from '../../../utils/form';
import * as rootReducer from '../../../reducers/root';
import * as store from '../../../reducers/store';
import * as logger from '../../../logger';

import {
  getIntegerParamFromLocationSearch,
  setPagePart,
} from '../../../utils/navigation';
import { Disclosure } from './Disclosure';
import { DocumentList, SigningFor } from './DocumentList';
import { SignatureInput } from './SignatureInput';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../utils/formSet';

import styles from './index.module.css';

interface StateProps {
  forms: store.Forms;
  patient: api.Patient.Patient | null;
  practicePreference: api.Previsit.PracticePreference;
}

interface DispatchProps {
  setFormStatus: (
    formSetId: string,
    formId: string,
    status: api.PrevisitForm.Status,
  ) => void;
}

interface Props
  extends RouteComponentProps,
    StateProps,
    DispatchProps,
    WithTranslation {
  navigateToNextPage: () => Promise<void>;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  setFormField: (formFieldId: string, value: string) => void;
  practiceName: string;
  documents: api.PrevisitDocument.DocumentWithTemplate[];
}

interface StoredValues {
  signatureText: string;
  disclosureAgreed: boolean;
  signingFor: SigningFor | null;
  guardianRelationship: string;
}

interface State extends StoredValues {
  documentsError: boolean;
  selectedDocuments: string[];
  currentPart: DocumentSignerPart;
  showDisclosure: boolean;
  pending: boolean;
}

enum DocumentSignerPart {
  DocumentList = 1,
  Signature = 2,
}

const MAX_PARTS = 2;

class DocumentSignerPageComponent extends React.Component<Props, State> {
  state: State = {
    disclosureAgreed: false,
    signingFor: null,
    guardianRelationship: '',
    documentsError: false,
    selectedDocuments: [],
    signatureText: '',
    currentPart: DocumentSignerPart.DocumentList,
    showDisclosure: false,
    pending: false,
  };

  handleChangePart = (part: DocumentSignerPart) => {
    const { history, location } = this.props;
    setPagePart(history, location, part);
  };

  handleAddSignature = () => {
    this.handleChangePart(DocumentSignerPart.Signature);
  };

  handleShowDisclosures = () => {
    this.setState({ showDisclosure: true });
  };

  handleSkip = async () => {
    const { field, documents, navigateToNextPage } = this.props;
    const { pending } = this.state;
    if (pending) {
      logger.error(
        new Error(
          `Document signer trying to skip signature while already pending!`,
        ),
      );
      return;
    }
    try {
      this.setState((prevState) => ({ ...prevState, pending: true }));
      await this.saveFormField('', true);
      const skippedDocuments = documents.map(({ id }) => id);
      await api.client.PrevisitDocumentTemplate.generateSignedDocuments({
        formSetId: field.previsitFormSetId,
        formFieldId: field.id,
        documentNotSignedIds: skippedDocuments,
      });
      await this.completeForm();
      await navigateToNextPage();
    } catch (e) {
      logger.error(e, `Document signer handleSkip failed`);
    } finally {
      this.setState((prevState) => ({ ...prevState, pending: false }));
    }
  };

  async saveFormField(signature: string, declineSigning: boolean = false) {
    const { field } = this.props;

    this.setState({
      signatureText: signature,
    });

    if (declineSigning) {
      this.setState({
        disclosureAgreed: false,
        signingFor: null,
        guardianRelationship: '',
      });
    }

    const value: StoredValues = declineSigning
      ? {
          disclosureAgreed: false,
          signingFor: null,
          signatureText: signature,
          guardianRelationship: this.state.guardianRelationship,
        }
      : {
          disclosureAgreed: true,
          signingFor: this.state.signingFor,
          signatureText: signature,
          guardianRelationship: this.state.guardianRelationship,
        };

    const serialized = JSON.stringify(value);

    this.props.setFormField(field.id, serialized);

    await api.client.Previsit.updateFormField(
      field.previsitFormSetId,
      field.id,
      serialized,
    );
  }

  async completeForm() {
    const { field, forms, setFormStatus } = this.props;
    const status = api.PrevisitForm.Status.Completed;
    await FormUtils.maybeUpdateGeneratedFormStatus(
      field.previsitFormId,
      forms,
      status,
    );
    setFormStatus(
      field.previsitFormSetId,
      FormUtils.DOCUMENT_FORM_NAME,
      status,
    );
  }

  initNavigationPart() {
    const { history, location } = this.props;
    const part = getIntegerParamFromLocationSearch(location.search, 'part');

    if (part !== 1) {
      setPagePart(history, location, DocumentSignerPart.DocumentList);
      return;
    }
    this.setCurrentPart();
  }

  setCurrentPart = () => {
    const { location } = this.props;
    const part =
      getIntegerParamFromLocationSearch(location.search, 'part') || 1;
    if (part >= 1 && part <= MAX_PARTS && this.state.currentPart !== part) {
      this.setState({ currentPart: part });
    }
  };

  componentDidMount() {
    const { field, practicePreference, documents } = this.props;
    if (practicePreference.previsitDefaultCheckRequiredDocuments) {
      this.setState({
        selectedDocuments:
          practicePreference.previsitDefaultCheckRequiredDocuments
            ? documents.filter((doc) => doc.required).map((doc) => doc.id)
            : [],
      });
    }
    if (field.value) {
      try {
        const parsed: StoredValues = JSON.parse(field.value);
        this.setState((state) => ({ ...state, parsed }));
      } catch (err) {
        logger.error(err, 'Error parsing data');
      }
    }
    this.initNavigationPart();
  }

  componentDidUpdate = () => {
    this.setCurrentPart();
  };

  handleSubmitSignature = async (signature: string) => {
    const { field, navigateToNextPage } = this.props;
    const { selectedDocuments, pending } = this.state;
    if (pending) {
      logger.error(
        new Error(
          `Document signer trying to submit signature while already pending!`,
        ),
      );
      return;
    }
    try {
      this.setState((prevState) => ({ ...prevState, pending: true }));
      await this.saveFormField(signature);
      const skippedDocumentIds = this.props.documents
        .filter(({ id }) => !selectedDocuments.includes(id))
        .map(({ id }) => id);

      await api.client.PrevisitDocumentTemplate.generateSignedDocuments({
        formSetId: field.previsitFormSetId,
        formFieldId: field.id,
        documentNotSignedIds: skippedDocumentIds,
        ...(selectedDocuments.length && {
          documentToSignIds: selectedDocuments,
        }),
      });
      await this.completeForm();
      await navigateToNextPage();
    } catch (e) {
      logger.error(e, `Document signer handleSubmitSignature failed`);
    } finally {
      this.setState((prevState) => ({ ...prevState, pending: false }));
    }
  };

  handleChangeSigningFor = (value: SigningFor) => {
    if (value === SigningFor.Myself) {
      this.setState({
        signingFor: value,
        // If the allowSelfForGuardianRelationship practice preference is set, we want to put 'Self'
        // in the guardian relationship field. Otherwise, we want to leave it blank.
        guardianRelationship: this.props.practicePreference
          .allowSelfForGuardianRelationship
          ? api.Previsit.relationshipMap.SELF
          : '',
      });
    } else {
      this.setState({
        signingFor: value,
      });
    }
  };

  handleGuardianRelationshipUpdate = (value: string) => {
    this.setState({
      guardianRelationship: value,
    });
  };

  render() {
    const { currentPart: currentPage, showDisclosure } = this.state;
    if (this.state.documentsError) {
      return <div className={styles.IndexMessage}>Error loading documents</div>;
    }

    if (showDisclosure) {
      return (
        <Disclosure
          practiceName={this.props.practiceName}
          onClose={() => {
            this.setState({ showDisclosure: false });
          }}
        />
      );
    }

    switch (currentPage) {
      case DocumentSignerPart.DocumentList:
        return (
          <div className={styles.SignerPage}>
            <DocumentList
              documents={this.props.documents}
              handleSkip={this.handleSkip}
              hasSignature={!!this.state.signatureText}
              onChangeSigningFor={this.handleChangeSigningFor}
              onClickDisclosures={this.handleShowDisclosures}
              onClickSign={this.handleAddSignature}
              selectedDocuments={this.state.selectedDocuments}
              setSelectedDocuments={(selectedDocuments: string[]) =>
                this.setState({ selectedDocuments })
              }
              signingFor={this.state.signingFor}
              onGuardianRelationshipUpdate={
                this.handleGuardianRelationshipUpdate
              }
              guardianRelationship={this.state.guardianRelationship}
              pending={this.state.pending}
            />
          </div>
        );
      case DocumentSignerPart.Signature:
        return (
          <SignatureInput
            onSubmit={this.handleSubmitSignature}
            initialSignature={this.state.signatureText}
            pending={this.state.pending}
          />
        );
    }
  }
}

const mapStateToProps = (store: store.FormSetSelected): StateProps => ({
  forms: getSelectedForms(store.formSets, store.selectedFormSetId),
  patient: store.loggedInPatient,
  practicePreference: getSelectedPracticePreference(store),
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormStatus: (
      formSetId: string,
      formId: string,
      status: api.PrevisitForm.Status,
    ) => dispatch(rootReducer.setFormStatus({ formSetId, formId, status })),
  };
}

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withRouter(DocumentSignerPageComponent)),
);
